import { Badge, Group, Loader, Pagination, SegmentedControl, Select, Stack, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconAlertTriangle, IconFileDescription } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  listKnowledgeUnits,
  type KnowledgeProposalType,
  type KnowledgeUnitSummary,
} from '@mygpt/app/apis/services/knowledgeLoop';
import { UnitDrawer } from './UnitDrawer';
import { CARD_BORDER, formatConfidence, formatJst, formatJstDate, proposalColor, signalColor } from './shared';

const PAGE_SIZE = 20;
type Filter = 'all' | KnowledgeProposalType;
type Sort = 'newest' | 'confidence' | 'evidence';

interface Props {
  datasetId: string;
  onChanged?: () => void;
}

/** 審査キュー：AI が出した提案（新規・更新・競合）を 1 件ずつ確認する */
export const ReviewQueue = ({ datasetId, onChanged }: Props) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<Filter>('all');
  const [sort, setSort] = useState<Sort>('newest');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<KnowledgeUnitSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listKnowledgeUnits(datasetId, {
        status: 'candidate',
        proposal_type: filter === 'all' ? undefined : filter,
        sort,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error('load review queue failed:', e);
      showNotification({ color: 'red', message: t('knowledge_loop.load_error') });
    } finally {
      setLoading(false);
    }
  }, [datasetId, filter, sort, page, t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <SegmentedControl
          size="xs"
          value={filter}
          onChange={(v) => {
            setFilter(v as Filter);
            setPage(1);
          }}
          data={(['all', 'add', 'update', 'conflict'] as Filter[]).map((f) => ({
            value: f,
            label: f === 'all' ? t('knowledge_loop.queue.filter_all') : t(`knowledge_loop.proposal.${f}`),
          }))}
        />
        <Group gap={8}>
          <Text size="xs" c="gray.6">
            {t('knowledge_loop.queue.total', { count: total })}
          </Text>
          <Select
            size="xs"
            w={150}
            allowDeselect={false}
            value={sort}
            onChange={(v) => v && setSort(v as Sort)}
            data={(['newest', 'confidence', 'evidence'] as Sort[]).map((s) => ({
              value: s,
              label: t(`knowledge_loop.queue.sort_${s}`),
            }))}
          />
        </Group>
      </Group>

      {loading ? (
        <Group justify="center" py={60}>
          <Loader size="sm" />
        </Group>
      ) : items.length === 0 ? (
        <Stack align="center" gap={4} py={60}>
          <Text fw={500}>{t('knowledge_loop.queue.empty')}</Text>
          <Text size="xs" c="gray.6">
            {t('knowledge_loop.queue.empty_desc')}
          </Text>
        </Stack>
      ) : (
        <Stack gap="sm">
          {items.map((u) => (
            <div
              key={u.id}
              role="button"
              tabIndex={0}
              onClick={() => setSelected(u.id)}
              onKeyDown={(e) => e.key === 'Enter' && setSelected(u.id)}
              style={{
                border: CARD_BORDER,
                borderRadius: 12,
                background: '#fff',
                padding: '12px 16px',
                cursor: 'pointer',
              }}
            >
              <Group justify="space-between" wrap="nowrap" align="flex-start">
                <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
                  <Group gap={6}>
                    <Badge size="sm" variant="light" color={proposalColor(u.proposal_type)}>
                      {t(`knowledge_loop.proposal.${u.proposal_type}`)}
                    </Badge>
                    {u.publish_form === 'document' && (
                      <Badge size="sm" variant="outline" color="gray" leftSection={<IconFileDescription size={10} />}>
                        {t('knowledge_loop.form.document')}
                      </Badge>
                    )}
                    {u.signals.map((s) => (
                      <Badge key={s} size="xs" variant="dot" color={signalColor(s)}>
                        {t(`knowledge_loop.signal.${s}`)}
                      </Badge>
                    ))}
                    {!!u.fact_changes?.length && (
                      <Badge size="xs" variant="light" color="orange">
                        {t('knowledge_loop.queue.fact_change', { count: u.fact_changes.length })}
                      </Badge>
                    )}
                    {!!u.pii_masked && (
                      <Badge size="xs" variant="light" color="orange" style={{ textTransform: 'none' }}>
                        {t('knowledge_loop.pii.masked_short')}
                      </Badge>
                    )}
                    {u.has_contradictions && (
                      <Badge size="xs" variant="filled" color="red">
                        {t('knowledge_loop.queue.contradiction')}
                      </Badge>
                    )}
                    {u.effective_from && (
                      <Badge size="xs" variant="outline" color="teal">
                        {t('knowledge_loop.queue.effective_from', { date: formatJstDate(u.effective_from) })}
                      </Badge>
                    )}
                    {u.has_uncertainties && (
                      <Badge size="xs" variant="light" color="yellow" leftSection={<IconAlertTriangle size={10} />}>
                        {t('knowledge_loop.queue.uncertain')}
                      </Badge>
                    )}
                  </Group>
                  <Text fw={600} size="sm" lineClamp={1}>
                    {u.title}
                  </Text>
                  <Text size="xs" c="gray.7" lineClamp={2}>
                    {u.body}
                  </Text>
                </Stack>
                <Stack gap={2} align="flex-end" style={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                  <Text size="xs" c="gray.6">
                    {t('knowledge_loop.review.evidence_count', { count: u.evidence_count })}
                  </Text>
                  <Text size="xs" c="gray.6">
                    {t('knowledge_loop.review.confidence')} {formatConfidence(u.confidence)}
                  </Text>
                  <Text size="xs" c="gray.5">
                    {formatJst(u.created_at)}
                  </Text>
                </Stack>
              </Group>
            </div>
          ))}
          {total > PAGE_SIZE && (
            <Group justify="center">
              <Pagination size="sm" value={page} onChange={setPage} total={Math.ceil(total / PAGE_SIZE)} />
            </Group>
          )}
        </Stack>
      )}

      <UnitDrawer
        datasetId={datasetId}
        unitId={selected}
        onClose={() => setSelected(null)}
        onReviewed={() => {
          load();
          onChanged?.();
        }}
      />
    </Stack>
  );
};
